import React from "react";
import PropTypes from "prop-types";
import { useVehicleTypes, useVehicleBrands } from "../../hooks/useVehicleQuery";
import SortDropdown from "../ui/dropdawn/SortDropdown";
import CustomInputField from "../ui/inputs/CustomInputField";
import "../../assets/scss/components/vehicle/VehicleInfoSection.scss";
import { useSearchablePagination } from "../../hooks/search-and-pagination/useSearchablePagination";
import { useVehicleSettingsOperations } from "../../hooks/useVehicleSettingsOperations";

export default function VehicleInfoSection({
  vehicleInfo,
  onChange,
  theme,
  isTrailer,
  viewMode = "dual", // Añadido para soportar modo de vista
}) {
  const { data: typesData } = useVehicleTypes();
  const { useSatelliteProviders } = useVehicleSettingsOperations();
  const { data: providersData, isLoading: providersLoading } = useSatelliteProviders();
  
  // Configuración de búsqueda para marcas
  const brandsSearch = useSearchablePagination(
    useVehicleBrands,
    [],
    {
      itemsPerPage: 5,
      enableSearch: true,
      enablePagination: true
    }
  );

  const vehicleTypes = typesData?.data || [];

  // Filtrar tipos de vehículo según sea cabezote o remolque
  const filteredTypes = isTrailer
    ? vehicleTypes.filter((type) => type.category?.includes("Remolque"))
    : vehicleTypes;

  const vehicleLabel = isTrailer ? "Remolque" : "Vehículo";

  // Mapear datos para el dropdown de marcas
  const brandOptions = brandsSearch.data.map((brand) => ({
    value: brand.id,
    label: brand.name,
  }));

  // Mapear proveedores GPS
  const gpsProviderOptions = [
    { value: "", label: "Sin proveedor GPS" },
    ...(providersData?.data || []).map((provider) => ({
      value: provider.id,
      label: provider.provider_name.charAt(0).toUpperCase() + provider.provider_name.slice(1),
    }))
  ];


  const handleBrandChange = (value) => {
    onChange("brand", value);
  };

  const handleTypeChange = (value) => {
    onChange("vehicleTypeId", value);
  };

  const handlePlateChange = (value) => {
    const upperCaseValue = value.toUpperCase();
    onChange("plate", upperCaseValue);
  };
  // Handler para cambio de proveedor GPS
  const handleGpsProviderChange = (value) => {
    onChange("gpsProviderId", value);
  };

  // Determinar si mostrar selector GPS
  const shouldShowGpsSelector = () => {
    // No mostrar para trailers
    if (isTrailer) return false;
    
    // No mostrar si el tipo de vehículo es remolque
    if (vehicleInfo.vehicleTypeId === 'fc52271b-d9a9-47ac-b734-8aaa38bf5edf') return false;
    
    // Verificar si el tipo seleccionado es remolque
    const selectedType = vehicleTypes.find(type => type.id === vehicleInfo.vehicleTypeId);
    if (selectedType?.category?.includes("Remolque")) return false;
    
    return true;
  };

  return (
    <div className={`GM__${theme}-vehicle-info-section ${viewMode}`}>
      <div className={`GM__${theme}-info-header`}>
        <h2 className={`GM__${theme}-section-title`}>
          Información del {vehicleLabel}
        </h2>
        <div className={`GM__${theme}-title-underline`}></div>
      </div>

      <div className={`GM__${theme}-info-container`}>
        {/* Selector de tipo para trailers */}
        
      {isTrailer && (
        <div className={`GM__${theme}-input-group`}>
          <label htmlFor="vehicleTypeId" className={`GM__${theme}-input-label`}>
            Tipo de Remolque
          </label>
          <SortDropdown
            id="vehicleTypeId"
            value={vehicleInfo.vehicleTypeId}
            onChange={handleTypeChange}
            options={filteredTypes.map((type) => ({
              value: type.id,
              label: type.name,
            }))}
            theme={theme}
            placeholder="Seleccione tipo de remolque"
          />
        </div>
      )}

        {/* Grid principal de información */}
        <div className={`GM__${theme}-info-grid`}>
          {/* Información básica */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>Datos básicos</h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-field-row`}>
                <div className={`GM__${theme}-input-group`}>
                  <label htmlFor="plate" className={`GM__${theme}-input-label`}>
                    Placa *
                  </label>
                  <CustomInputField
                    id="plate"
                    type="text"
                    name="plate"
                    value={vehicleInfo.plate}
                    onChange={(e) => handlePlateChange(e.target.value)}
                    placeholder="Ej. ABC123"
                    theme={theme}
                  />
                </div>

                <div className={`GM__${theme}-input-group`}>
                  <label htmlFor="brand" className={`GM__${theme}-input-label`}>
                    Marca *
                  </label>
                  <SortDropdown
                    id="brand"
                    value={vehicleInfo.brand}
                    onChange={handleBrandChange}
                    options={brandOptions}
                    theme={theme}
                    placeholder="Seleccione una marca"
                    enableSearch={true}
                    enablePagination={true}
                    onSearch={brandsSearch.handleSearch}
                    searchPlaceholder="Buscar marca..."
                    itemsPerPage={brandsSearch.itemsPerPage}
                    totalItems={brandsSearch.totalItems}
                    currentPage={brandsSearch.currentPage}
                    onPageChange={brandsSearch.handlePageChange}
                    searchTerm={brandsSearch.searchTerm}
                    isLoading={brandsSearch.isLoading}
                  />
                </div>
              </div>

              <div className={`GM__${theme}-field-row`}>
                <div className={`GM__${theme}-input-group`}>
                  <label htmlFor="model" className={`GM__${theme}-input-label`}>
                    Modelo *
                  </label>
                  <CustomInputField
                    id="model"
                    type="text"
                    name="model"
                    value={vehicleInfo.model}
                    onChange={(e) => onChange("model", e.target.value)}
                    placeholder="Ej. GL 2023"
                    theme={theme}
                  />
                </div>

                <div className={`GM__${theme}-input-group`}>
                  <label htmlFor="year" className={`GM__${theme}-input-label`}>
                    Año
                  </label>
                  <CustomInputField
                    id="year"
                    type="number"
                    name="year"
                    value={vehicleInfo.year}
                    onChange={(e) => onChange("year", e.target.value)}
                    placeholder="Ej. 2023"
                    theme={theme}
                    min="1900"
                    max={new Date().getFullYear() + 1}
                  />
                </div>
              </div>

              <div className={`GM__${theme}-field-row`}>
                <div className={`GM__${theme}-input-group`}>
                  <label htmlFor="capacity" className={`GM__${theme}-input-label`}>
                    Capacidad (toneladas)
                  </label>
                  <CustomInputField
                    id="capacity"
                    type="number"
                    name="capacity"
                    value={vehicleInfo.capacity}
                    onChange={(e) => onChange("capacity", e.target.value)}
                    placeholder="Ej. 10"
                    theme={theme}
                    min="0"
                    step="0.1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Configuración */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>Configuración</h3>
            <div className={`GM__${theme}-card-content`}>
              {shouldShowGpsSelector() && (
                <div className={`GM__${theme}-gps-provider-section`}>
                  <div className={`GM__${theme}-input-group`}>
                    <label htmlFor="gpsProvider" className={`GM__${theme}-input-label`}>
                      Proveedor GPS Satelital
                    </label>
                    <SortDropdown
                      id="gpsProvider"
                      value={vehicleInfo.gpsProviderId || ""}
                      onChange={handleGpsProviderChange}
                      options={gpsProviderOptions}
                      theme={theme}
                      placeholder="Seleccione proveedor GPS"
                      disabled={providersLoading}
                    />
                  </div>
                  <div className={`GM__${theme}-gps-info-text`}>
                    <span className={`GM__${theme}-info-icon`}>ℹ️</span>
                    <span>Seleccione el proveedor de rastreo satelital del vehículo</span>
                  </div>
                </div>
              )}
              <div className={`GM__${theme}-toggles-section`}>
                <div className={`GM__${theme}-toggle-item`}>
                  <label className={`GM__${theme}-toggle-label`}>
                    <span className={`GM__${theme}-toggle-text`}>
                      Notificaciones habilitadas
                    </span>
                    <div className={`GM__${theme}-toggle-wrapper`}>
                      <input
                        type="checkbox"
                        checked={vehicleInfo.limitedNotifications}
                        onChange={(e) =>
                          onChange("limitedNotifications", e.target.checked)
                        }
                        className={`GM__${theme}-toggle-input`}
                      />
                      <span className={`GM__${theme}-toggle-slider`}></span>
                    </div>
                  </label>
                  <span className={`GM__${theme}-toggle-status`}>
                    {vehicleInfo.limitedNotifications ? "Activas" : "Inactivas"}
                  </span>
                </div>

                <div className={`GM__${theme}-toggle-item`}>
                  <label className={`GM__${theme}-toggle-label`}>
                    <span className={`GM__${theme}-toggle-text`}>
                      Flota propia
                    </span>
                    <div className={`GM__${theme}-toggle-wrapper`}>
                      <input
                        type="checkbox"
                        checked={vehicleInfo.ownFleet}
                        onChange={(e) => onChange("ownFleet", e.target.checked)}
                        className={`GM__${theme}-toggle-input`}
                      />
                      <span className={`GM__${theme}-toggle-slider`}></span>
                    </div>
                  </label>
                  <span className={`GM__${theme}-toggle-status`}>
                    {vehicleInfo.ownFleet ? "Sí" : "No"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

VehicleInfoSection.propTypes = {
  vehicleInfo: PropTypes.shape({
    vehicleTypeId: PropTypes.string,
    plate: PropTypes.string,
    brand: PropTypes.string,
    model: PropTypes.string,
    capacity: PropTypes.string,
    year: PropTypes.string,
    ownFleet: PropTypes.bool,
    limitedNotifications: PropTypes.bool,
    gpsProviderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
  isTrailer: PropTypes.bool,
  viewMode: PropTypes.oneOf(['simple', 'dual']),
};